import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../config';

const GoogleTracking = () => {
  const location = useLocation();
  const [tracking, setTracking] = useState(null);
  const [cookieConsent, setCookieConsent] = useState(null);

  // Fetch tracking settings din API
  useEffect(() => {
    const fetchTracking = async () => {
      try {
        const response = await fetch(`${API_URL}/api/orders/tracking`);
        const data = await response.json();
        setTracking(data);
      } catch (error) {
        console.error('Error fetching tracking settings:', error);
      }
    };

    fetchTracking();
  }, []);

  // Citește cookie consent din localStorage
  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (consent) {
      try {
        const parsed = JSON.parse(consent);
        setCookieConsent(parsed);
      } catch (error) {
        // Backwards compatibility: format vechi era string simplu "accepted"/"rejected"
        // Migrăm automat la format nou JSON
        if (consent === 'accepted' || consent === 'rejected') {
          const migrated = {
            necessary: true,
            statistics: consent === 'accepted',
            marketing: consent === 'accepted',
            preferences: consent === 'accepted'
          };
          localStorage.setItem('cookieConsent', JSON.stringify(migrated));
          setCookieConsent(migrated);
        } else {
          console.error('Error parsing cookie consent:', error);
        }
      }
    }

    // Listen pentru modificări cookie consent (când user acceptă/refuză)
    const handleStorageChange = (e) => {
      if (e.key === 'cookieConsent' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          setCookieConsent(parsed);
        } catch (error) {
          console.error('Error parsing cookie consent:', error);
        }
      }
    };

    // Listen pentru event custom din CookieBanner (același tab)
    const handleConsentChanged = () => {
      const consent = localStorage.getItem('cookieConsent');
      if (consent) {
        try {
          const parsed = JSON.parse(consent);
          setCookieConsent(parsed);
        } catch (error) {
          console.error('Error parsing cookie consent:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('cookieConsentChanged', handleConsentChanged);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('cookieConsentChanged', handleConsentChanged);
    };
  }, []);

  // Injectare Google Analytics 4
  useEffect(() => {
    if (!tracking?.googleAnalytics?.enabled) return;
    if (!tracking.googleAnalytics.measurementId) return;
    if (!cookieConsent?.statistics) return;

    const measurementId = tracking.googleAnalytics.measurementId;

    // Încarcă GA4 o singură dată per pagină (verificăm scriptul, nu window.gtag - GTM poate defini și el gtag)
    if (document.getElementById('ga4-script')) return;

    // Fără Analytics pe paginile de admin: nu încărcăm deloc dacă intrarea e direct în admin
    if (window.location.pathname.startsWith('/admin')) return;

    // Dacă se trece din site în admin (SPA), comutatorul oficial Google ga-disable-<ID> oprește trimiterea.
    // Îl setăm ÎNAINTE ca GA să vadă schimbarea de pagină (wrapper pe history, instalat înaintea GA).
    const setAdminOptOut = (url) => {
      try {
        window[`ga-disable-${measurementId}`] = new URL(url, window.location.href).pathname.startsWith('/admin');
      } catch (e) {}
    };
    ['pushState', 'replaceState'].forEach((fn) => {
      const original = window.history[fn];
      window.history[fn] = function (state, title, url) {
        if (url) setAdminOptOut(url);
        return original.apply(this, arguments);
      };
    });
    window.addEventListener('popstate', () => setAdminOptOut(window.location.href));

    // Injectare script GA4
    const script = document.createElement('script');
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    script.async = true;
    script.id = 'ga4-script';
    document.head.appendChild(script);

    // Inițializare gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', measurementId);

    // Fără cleanup: ștergerea dataLayer la re-randare rupea legătura cu GA4
    // și toate evenimentele de după page_view se pierdeau.
  }, [tracking, cookieConsent]);

  // Injectare Google Tag Manager
  useEffect(() => {
    if (!tracking?.googleTagManager?.enabled) return;
    if (!tracking.googleTagManager.containerId) return;
    if (!cookieConsent?.statistics) return;

    const containerId = tracking.googleTagManager.containerId;

    // Încarcă GTM o singură dată per pagină
    if (document.getElementById('gtm-script-head') || window.google_tag_manager) return;

    // Injectare GTM <head> script
    const scriptHead = document.createElement('script');
    scriptHead.id = 'gtm-script-head';
    scriptHead.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${containerId}');
    `;
    document.head.appendChild(scriptHead);

    // Injectare GTM <body> noscript iframe
    const noscript = document.createElement('noscript');
    noscript.id = 'gtm-noscript-body';
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${containerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.insertBefore(noscript, document.body.firstChild);

    // Fără cleanup: la re-randare GTM ar fi fost încărcat a doua oară
  }, [tracking, cookieConsent]);

  // (excluderea reală a paginilor admin din Analytics e în efectul GA4 de mai sus)
  const isAdminPage = location.pathname.startsWith('/admin');
  if (isAdminPage) return null;

  // Componenta nu renderează nimic (doar side effects)
  return null;
};

export default GoogleTracking;
